import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Container, Card, Button, Row, Col, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const GalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const translations = {
    'Gallery': 'معرض الصور',
    'Photo Gallery & Media Assets': 'معرض الصور والمواد الإعلامية',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'No images found': 'لم يتم العثور على صور',
    'Error loading gallery': 'خطأ في تحميل المعرض',
    'Loading gallery...': 'جاري تحميل المعرض...',
    'By': 'بواسطة',
    'Published on': 'نشر في',
    'Field Photos': 'صور ميدانية',
    'Events': 'فعاليات',
    'Portraits': 'بورتريه',
    'Close': 'إغلاق',
    'All': 'الكل',
    'View Image': 'عرض الصورة',
    'Description': 'الوصف',
    'View Details': 'عرض التفاصيل'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '3rem 2rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(220, 53, 69, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#1a1a1a',
      padding: '160px 20px 80px',
      background: `linear-gradient(135deg, 
        rgba(220, 53, 69, 0.05) 0%, 
        rgba(220, 53, 69, 0.1) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #dc3545 0%, #1a1a1a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 5px 15px rgba(220, 53, 69, 0.2)',
      letterSpacing: '-1px',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: '1.3rem',
      marginBottom: '2rem',
      color: '#dc3545',
      fontWeight: '500',
      maxWidth: '800px',
      margin: '0 auto 2rem',
      lineHeight: '1.5'
    },
    card: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '15px',
      textAlign: 'center',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '250px',
      width: '100%',
      cursor: 'pointer'
    },
    cardHover: {
      transform: 'translateY(-8px) scale(1.02)',
      boxShadow: '0 15px 30px rgba(220, 53, 69, 0.15)',
      borderColor: '#dc3545',
      backgroundColor: '#fff5f5'
    },
    cardTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '0.5rem',
      textAlign: 'center'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(220, 53, 69, 0.1)',
      borderRadius: '50%'
    }
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!db1) {
          throw new Error('Firebase is not properly initialized.');
        }
        
        const galleryQuery = query(
          collection(db1, 'galleries'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(galleryQuery);
        
        const galleryList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.featuredImage) {
            galleryList.push({
              id: doc.id,
              token: data.token || doc.id, // Use token if available, fallback to id
              imageUrl: data.featuredImage,
              title: data.title,
              description: data.excerpt,
              category: 'field_photos',
              author: data.author,
              createdAt: data.createdAt?.toDate() || new Date(),
              views: data.views || 0,
              likes: data.likes || 0
            });
          }
        });

        setGalleryItems(galleryList.slice(0, 12));
        
      } catch (err) {
        console.error('Error fetching gallery:', err);
        setError(`${translateText('Error loading gallery')}: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryBadge = (category) => {
    const categories = {
      'field_photos': { text: translateText('Field Photos'), color: '#dc3545' },
      'events': { text: translateText('Events'), color: '#28a745' },
      'portraits': { text: translateText('Portraits'), color: '#007bff' }
    };
    return categories[category] || { text: 'Image', color: '#6c757d' };
  };

  const openImageModal = (item) => {
    setSelectedImage(item);
    setShowModal(true);
  };

  const navigateToGalleryPost = (item) => {
    // Use token for navigation instead of id
    navigate(`/gallery/${item.token}`);
  };

  const handleCardClick = (item, e) => {
    // If the click is on the "View Details" button, navigate to the post
    // Otherwise, open the image modal
    if (e.target.closest('.view-details-btn')) {
      navigateToGalleryPost(item);
    } else {
      openImageModal(item);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#dc3545' }} />
            <p className="mt-3" style={{ color: '#dc3545' }}>
              {translateText('Loading gallery...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.container}>
        {/* Floating Background Shapes */}
        <div style={styles.floatingShapes}>
          <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(220, 53, 69, 0.05)'}}></div>
          <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(220, 53, 69, 0.08)'}}></div>
          <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(220, 53, 69, 0.03)'}}></div>
        </div>

        {/* Language Toggle Button */}
        <div style={styles.languageToggle}>
          <button 
            onClick={() => setIsArabic(!isArabic)}
            style={styles.toggleButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
              e.target.style.boxShadow = '0 8px 20px rgba(26, 26, 26, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#dc3545';
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
            }}
          >
            {isArabic ? 'ENGLISH' : 'العربية'}
          </button>
        </div>

        {/* Header */}
        <header style={styles.header}>
          <h1 style={styles.title}>{translateText('Gallery')}</h1>
          <p style={styles.subtitle}>
            {translateText('Photo Gallery & Media Assets')}
          </p>
        </header>

        {/* Main Content */}
        <div style={styles.content}>
          {error && (
            <Alert variant="danger" className="mb-4">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            </Alert>
          )}

          {galleryItems.length === 0 && !error ? (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', color: '#dee2e6', marginBottom: '1rem' }}>
                🖼️
              </div>
              <h4 style={{ color: '#666' }}>{translateText('No images found')}</h4>
              <p className="text-muted">Check back later for new gallery content</p>
            </div>
          ) : (
            <Row>
              {galleryItems.map((item, index) => {
                const badgeInfo = getCategoryBadge(item.category);
                return (
                  <Col key={item.token} lg={3} md={4} sm={6} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm" 
                      style={{ 
                        border: 'none',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        ...(hoveredCard === index ? styles.cardHover : {})
                      }}
                      onClick={(e) => handleCardClick(item, e)}
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <div className="position-relative">
                        <Card.Img 
                          variant="top" 
                          src={item.imageUrl}
                          style={{ 
                            height: '200px', 
                            objectFit: 'cover',
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px'
                          }}
                        />
                        <div className="position-absolute top-0 start-0 m-2">
                          <Badge style={{ backgroundColor: badgeInfo.color, fontSize: '0.7rem' }}>
                            {badgeInfo.text}
                          </Badge>
                        </div>
                      </div>
                      
                      <Card.Body className="d-flex flex-column p-3">
                        <Card.Title style={{ 
                          color: '#dc3545',
                          fontWeight: '600',
                          lineHeight: '1.4',
                          fontSize: '1rem',
                          minHeight: '40px'
                        }}>
                          {item.title && item.title.length > 50 ? item.title.substr(0, 50) + '...' : item.title}
                        </Card.Title>

                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small style={{ color: '#666', fontWeight: '500' }}>
                              <i className="bi bi-person me-1"></i>
                              {item.author || 'Al-Majd Media'}
                            </small>
                            <small style={{ color: '#666' }}>
                              <i className="bi bi-calendar me-1"></i>
                              {formatDate(item.createdAt)}
                            </small>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-3 text-muted">
                              <small>
                                <i className="bi bi-eye me-1"></i>
                                {item.views || 0}
                              </small>
                              <small>
                                <i className="bi bi-hand-thumbs-up me-1"></i>
                                {item.likes || 0}
                              </small>
                            </div>
                            <div className="d-flex gap-2">
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                className="view-details-btn"
                                style={{ 
                                  color: '#dc3545', 
                                  borderColor: '#dc3545',
                                  borderRadius: '20px',
                                  fontWeight: '500',
                                  fontSize: '0.8rem'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToGalleryPost(item);
                                }}
                              >
                                {translateText('View Details')}
                                <i className="bi bi-arrow-right ms-1"></i>
                              </Button>
                              <Button 
                                variant="outline-secondary"
                                size="sm"
                                style={{ 
                                  color: '#6c757d', 
                                  borderColor: '#6c757d',
                                  borderRadius: '20px',
                                  fontWeight: '500',
                                  fontSize: '0.8rem'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openImageModal(item);
                                }}
                              >
                                {translateText('View Image')}
                                <i className="bi bi-zoom-in ms-1"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </div>

      {/* Image Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#dc3545' }}>
            {selectedImage?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <>
              <img 
                src={selectedImage.imageUrl} 
                alt={selectedImage.title}
                style={{ 
                  width: '100%', 
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
              {selectedImage.description && (
                <div className="mt-3">
                  <h6 style={{ color: '#dc3545' }}>{translateText('Description')}:</h6>
                  <p style={{ color: '#666' }}>{selectedImage.description}</p>
                </div>
              )}
              <div className="d-flex justify-content-between text-muted mt-3">
                <small>
                  <i className="bi bi-person me-1"></i>
                  {selectedImage.author}
                </small>
                <small>
                  <i className="bi bi-calendar me-1"></i>
                  {formatDate(selectedImage.createdAt)}
                </small>
              </div>
              <div className="mt-4">
                <Button 
                  variant="primary"
                  onClick={() => {
                    setShowModal(false);
                    navigateToGalleryPost(selectedImage);
                  }}
                  style={{ 
                    borderRadius: '20px', 
                    backgroundColor: '#dc3545', 
                    borderColor: '#dc3545',
                    fontWeight: '500'
                  }}
                >
                  {translateText('View Details')}
                  <i className="bi bi-arrow-right ms-2"></i>
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            style={{ borderRadius: '20px' }}
          >
            {translateText('Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GalleryPage;
